import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, List, X, Printer } from 'lucide-react';
import { slides, slideTitles } from './slides';
import { useScreenInit } from '../useScreenInit.js';
export function Deck() {
  const screenInit = useScreenInit();
  const [currentIndex, setCurrentIndex] = useState(typeof screenInit?.slide === 'number' ? screenInit.slide : 0);
  const [direction, setDirection] = useState(0);
  const [tocOpen, setTocOpen] = useState(false);
  const [printMode, setPrintMode] = useState(false);
  useEffect(() => {
    if (printMode) {
      // Give the DOM time to finish all Framer Motion entrance animations
      // and load images over the network before triggering the print dialog.
      const timer = setTimeout(() => {
        window.print();
      }, 2500);
      const handleAfterPrint = () => setPrintMode(false);
      window.addEventListener('afterprint', handleAfterPrint);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('afterprint', handleAfterPrint);
      };
    }
  }, [printMode]);
  useEffect(() => {
    if (printMode) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') {
        e.preventDefault();
        if (currentIndex < slides.length - 1) {
          setDirection(1);
          setCurrentIndex(prev => prev + 1);
        }
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        if (currentIndex > 0) {
          setDirection(-1);
          setCurrentIndex(prev => prev - 1);
        }
      } else if (e.key === 'Home') {
        e.preventDefault();
        setDirection(-1);
        setCurrentIndex(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        setDirection(1);
        setCurrentIndex(slides.length - 1);
      } else if (e.key === 'Escape') {
        setTocOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, printMode]);
  const goTo = (idx: number) => {
    setDirection(idx > currentIndex ? 1 : -1);
    setCurrentIndex(idx);
  };
  const goPrev = () => currentIndex > 0 && goTo(currentIndex - 1);
  const goNext = () => currentIndex < slides.length - 1 && goTo(currentIndex + 1);
  if (printMode) {
    return <>
        <div className="fixed inset-0 z-[100] bg-white flex flex-col items-center justify-center hide-on-print" data-id="element-1" data-mp="2qoei0ssyhpu9">
          <div className="w-12 h-12 border-4 border-[var(--brand)]/20 border-t-[var(--brand)] rounded-full animate-spin mb-4" data-id="element-2" data-mp="2vmfyd5yzzb21" />
          <p className="text-xl font-bold text-[var(--fg)] animate-pulse" data-id="element-3" data-mp="2lq34a6zjiv6r">
            Preparing PDF...
          </p>
          <p className="text-sm text-[var(--muted)] mt-2" data-id="element-4" data-mp="f182wr0qs0jl">
            Waiting for animations and images to load
          </p>
        </div>
        <div className="print-container bg-white" data-id="element-5" data-mp="jx5igv46l2vb">
          {slides.map((Slide, idx) => <div key={idx} className="print-slide w-full h-full relative overflow-hidden break-after-page" data-id="element-6" data-mp="24hk2sxw6xdtl" data-mpi={idx}>
              <Slide index={idx} total={slides.length} data-id="element-7" data-mp="2e7loxk4zgm5v" />
            </div>)}
        </div>
      </>;
  }
  const CurrentSlide = slides[currentIndex];
  return <div className="relative w-screen h-screen overflow-hidden bg-[var(--bg)]" data-id="element-8" data-mp="29ekcdis90ypg">
      <AnimatePresence mode="wait" custom={direction} data-id="element-9" data-mp="2n9v1efjtaplw">
        <motion.div key={currentIndex} custom={direction} initial={{
        opacity: 0,
        x: direction > 0 ? 40 : -40
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: direction > 0 ? -40 : 40
      }} transition={{
        duration: 0.4,
        ease: [0.4, 0, 0.2, 1]
      }} className="absolute inset-0" data-id="element-10" data-mp="2j6ygujapojfb">
          <CurrentSlide index={currentIndex} total={slides.length} data-id="element-11" data-mp="zaspc74yx16v" />
        </motion.div>
      </AnimatePresence>

      {/* Top progress bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-black/5 z-40 hide-on-print" data-id="element-12" data-mp="14b6wlmyqw73u">
        <motion.div className="h-full bg-[var(--brand)]" animate={{
        width: `${(currentIndex + 1) / slides.length * 100}%`
      }} transition={{
        duration: 0.4,
        ease: [0.4, 0, 0.2, 1]
      }} data-id="element-13" data-mp="198yls9809f4c" />
      </div>

      {/* Top left controls */}
      <div className="absolute top-3 left-3 z-50 flex items-center gap-2 hide-on-print" data-id="element-14" data-mp="lg9tyfnfyxy2">
        <button onClick={() => setTocOpen(true)} aria-label="Open table of contents" className="flex h-8 items-center gap-1.5 rounded-full border border-black/10 bg-white/90 px-3 text-[var(--brand)] shadow-sm backdrop-blur transition-all hover:bg-white" data-id="element-15" data-mp="qfmg0y6ho0zl">
          <List className="h-3.5 w-3.5" data-id="element-16" data-mp="2q0cjyj32zls" />
          <span className="text-xs font-semibold" data-id="element-17" data-mp="2g2wd5y75fchr">Contents</span>
        </button>

        <button onClick={() => setPrintMode(true)} aria-label="Export to PDF" className="flex h-8 items-center gap-1.5 rounded-full border border-black/10 bg-white/90 px-3 text-[var(--brand)] shadow-sm backdrop-blur transition-all hover:bg-white" data-id="element-18" data-mp="2c12hk4bt8yog">
          <Printer className="h-3.5 w-3.5" data-id="element-19" data-mp="1x3ihnbtrttp5" />
          <span className="text-xs font-semibold" data-id="element-20" data-mp="1x4k3mbcbs0ln">PDF</span>
        </button>
      </div>

      {/* Bottom nav cluster */}
      <div className="absolute bottom-3 right-3 z-50 flex items-center gap-2 hide-on-print" data-id="element-21" data-mp="1sb8svz4e6zog">
        <button onClick={goPrev} disabled={currentIndex === 0} aria-label="Previous slide" className="flex h-8 w-8 items-center justify-center rounded-full border border-black/10 bg-white/90 text-[var(--brand)] shadow-sm backdrop-blur transition-all hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-22" data-mp="1obqaba01n0os">
          <ChevronLeft className="h-4 w-4" data-id="element-23" data-mp="zpskqqe1rt1b" />
        </button>
        <div className="flex items-baseline gap-1 rounded-full bg-[var(--brand)] px-3.5 py-1.5 shadow-md" data-id="element-24" data-mp="zsolemha4vtg">
          <span className="text-white font-bold text-sm tabular-nums" data-id="element-25" data-mp="qpnfalsez4ps">
            {currentIndex + 1}
          </span>
          <span className="text-[var(--brand-lighter)] text-xs font-medium" data-id="element-26" data-mp="uqpgv99szsux">
            / {slides.length}
          </span>
        </div>
        <button onClick={goNext} disabled={currentIndex === slides.length - 1} aria-label="Next slide" className="flex h-8 w-8 items-center justify-center rounded-full border border-black/10 bg-white/90 text-[var(--brand)] shadow-sm backdrop-blur transition-all hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-27" data-mp="1ii4qln3wtcg7">
          <ChevronRight className="h-4 w-4" data-id="element-28" data-mp="21ffjmpyy79n7" />
        </button>
      </div>

      {/* Collapsible TOC */}
      <AnimatePresence data-id="element-29" data-mp="267fs56t33ave">
        {tocOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.25
        }} onClick={() => setTocOpen(false)} className="absolute inset-0 z-[60] bg-black/40 backdrop-blur-sm" data-id="element-30" data-mp="3qwta69nwbi01" />
            <motion.nav initial={{
          x: '-100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '-100%'
        }} transition={{
          duration: 0.35,
          ease: [0.4, 0, 0.2, 1]
        }} aria-label="Table of contents" className="absolute top-0 left-0 bottom-0 z-[70] w-[min(22rem,85vw)] bg-white shadow-2xl flex flex-col" data-id="element-31" data-mp="37yqghhkekwvw">
              <div className="flex items-center justify-between px-6 py-5 border-b border-black/5" data-id="element-32" data-mp="s6jfn9ckqgi2">
                <div data-id="element-33" data-mp="2lrctuzvm5evg">
                  <p className="text-[var(--brand)] text-xs font-semibold uppercase tracking-wider" data-id="element-34" data-mp="2qomjafj05qja">
                    Brand Portfolio Suggestion
                  </p>
                  <h2 className="text-[var(--fg)] font-bold text-lg" data-id="element-35" data-mp="2vmeno90u354d">
                    Contents
                  </h2>
                </div>
                <button onClick={() => setTocOpen(false)} aria-label="Close table of contents" className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-[var(--surface)] text-[var(--muted)]" data-id="element-36" data-mp="35dsp4mccif05">
                  <X className="h-5 w-5" data-id="element-37" data-mp="3ece3e5asb7v4" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto py-2" data-id="element-38" data-mp="3jb7jm5hghcyz">
                {slideTitles.map((s, idx) => {
              const active = idx === currentIndex;
              return <button key={idx} onClick={() => {
                goTo(idx);
                setTocOpen(false);
              }} className={`w-full flex items-center gap-4 px-6 py-3 text-left transition-colors ${active ? 'bg-[var(--surface)]' : 'hover:bg-[var(--surface)]/60'}`} data-id="element-39" data-mp="1p1ddij981c2b" data-mpi={idx}>
                      <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${active ? 'bg-[var(--brand)] text-white' : 'bg-[var(--surface)] text-[var(--muted)]'}`} data-id="element-40" data-mp="12sor0cu3x6g1">
                        {idx + 1}
                      </span>
                      <span className="min-w-0" data-id="element-41" data-mp="1lhdacucxz3pp">
                        <span className={`block text-[0.65rem] font-semibold uppercase tracking-wider ${active ? 'text-[var(--brand-light)]' : 'text-[var(--muted)]'}`} data-id="element-42" data-mp="1lgk03jerxw2t">
                          {s.section}
                        </span>
                        <span className={`block text-sm font-semibold truncate ${active ? 'text-[var(--brand)]' : 'text-[var(--fg)]'}`} data-id="element-43" data-mp="2qeolu1wwbfwb">
                          {s.title}
                        </span>
                      </span>
                    </button>;
            })}
              </div>
            </motion.nav>
          </>}
      </AnimatePresence>
    </div>;
}